import { createContext, useContext, useMemo, type ReactNode } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

import { type DialogStackState } from './DialogStack.types';

/** The preset ships three stack rungs; a deeper dialog clamps to the last one. */
const Z_BY_DEPTH = ['z-modalStack1', 'z-modalStack2', 'z-modalStack3'] as const;

/**
 * A dialog with no provider above it is simply the only dialog — a `<Dialog>`
 * opened inside a page, not through the modal manager, still has to render.
 */
const LONE_DIALOG: DialogStackState = { depth: 0, total: 1 };

const DialogStackContext = createContext<DialogStackState>(LONE_DIALOG);

export function useDialogStack(): DialogStackState {
  return useContext(DialogStackContext);
}

export interface DialogStackProviderProps extends DialogStackState {
  children: ReactNode;
}

/**
 * Publishes a dialog's position in the stack.
 *
 * Position comes from the modal slice's array INDEX — mounted by
 * `GlobalModalManager`, never self-registered. A dialog that registers its own
 * depth from its component body counts itself even when it never mounts (a
 * guard bails, a query is still loading), and the parent then recedes behind
 * nothing.
 */
export function DialogStackProvider({ depth, total, children }: DialogStackProviderProps) {
  const value = useMemo(() => ({ depth, total }), [depth, total]);

  return <DialogStackContext.Provider value={value}>{children}</DialogStackContext.Provider>;
}

/**
 * The z-rung and the receded treatment for one dialog in the stack.
 *
 * A parent stays ON SCREEN when a child opens — dismissing the child has to
 * return the operator to it — but steps back and stops taking clicks, so which
 * dialog is live is never ambiguous.
 */
export function dialogStackClasses({ depth, total }: DialogStackState): string {
  const rung = Z_BY_DEPTH[Math.min(depth, Z_BY_DEPTH.length - 1)];
  const isBehind = depth < total - 1;

  return cn(rung, isBehind && 'scale-95 opacity-60 pointer-events-none');
}
